/**
 * General utility functions
 * Support center URLs, date formatting, and lodash replacements
 */

/** Language code mapping */
const LANGUAGE_MAP: Record<string, string> = {
  'de-DE': 'de',
  'fr-FR': 'fr',
  'it-IT': 'it',
  'es-ES': 'es',
  'pt-BR': 'pt-BR',
  'el-GR': 'el',
  'ja-JP': 'ja',
  'es-MX': 'es-MX',
  'nl-NL': 'nl-NL',
  'tr-TR': 'tr',
  'pt-PT': 'pt-PT',
  'pl-PL': 'pl',
  'en-GB': 'en-GB',
  'en-US': 'en-US',
}

/**
 * Get trivago Support Center URL with query parameters
 * @param language - User's language code
 * @param studioUserId - Optional user ID
 * @param itemId - Optional accommodation/item ID
 * @param isPaid - Optional paid status
 * @returns Support center URL with params
 */
export function getSupportCenterUrl(
  language: string,
  studioUserId: number | null = null,
  itemId: number | null = null,
  isPaid: boolean | null = null
): string {
  const paramsObject = {
    language: LANGUAGE_MAP[language] || 'en-US',
    studioUserId: studioUserId ? String(studioUserId) : null,
    itemId: itemId ? String(itemId) : null,
    isPaid: isPaid !== null ? String(Boolean(isPaid)) : null,
  }

  const cleanParams = Object.entries(paramsObject)
    .filter(([, value]) => value != null && value !== 'null' && value !== 'undefined')
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as Record<string, string>)

  const params = new URLSearchParams(cleanParams)

  return `https://trivago.my.site.com/trivagoSupport/?${params.toString()}`
}

/**
 * Get trivago Contact form URL with query parameters
 * @param language - User's language code
 * @param studioUserId - Optional user ID
 * @param itemId - Optional accommodation/item ID
 * @param isPaid - Optional paid status
 * @returns Contact form URL with params
 */
export function getContactUrl(
  language: string,
  studioUserId: number | null = null,
  itemId: number | null = null,
  isPaid: boolean | null = null
): string {
  const paramsObject = {
    language: LANGUAGE_MAP[language] || 'en-US',
    studioUserId: studioUserId ? String(studioUserId) : null,
    itemId: itemId ? String(itemId) : null,
    isPaid: isPaid !== null ? String(Boolean(isPaid)) : null,
  }

  const cleanParams = Object.entries(paramsObject)
    .filter(([, value]) => value != null && value !== 'null' && value !== 'undefined')
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {} as Record<string, string>)

  const params = new URLSearchParams(cleanParams)

  return `https://trivago.my.site.com/trivagoSupport/get-help?${params.toString()}`
}

/**
 * Get privacy policy URL for locale
 * @param lang - Locale code
 * @returns Privacy policy URL for locale
 */
export function getPrivacyPolicyUrl(lang: string): string {
  switch (lang) {
    case 'en-GB':
      return 'https://www.trivago.co.uk/en-GB/sp/privacy-policy'
    case 'en-US':
      return 'https://www.trivago.com/en-US/sp/privacy-policy'
    case 'nl-NL':
      return 'https://www.trivago.nl/nl/sp/privacybeleid'
    case 'pt-BR':
      return 'https://www.trivago.com.br/pt-BR/sp/politica-privacidade'
    case 'de-DE':
      return 'https://www.trivago.de/de/sp/datenschutzerklaerung'
    case 'es-ES':
      return 'https://www.trivago.es/es/sp/politica-de-privacidad'
    case 'fr-FR':
      return 'https://www.trivago.fr/fr/sp/politique-de-confidentialite'
    case 'el-GR':
      return 'https://www.trivago.gr/el/sp/%CF%80%CF%81%CE%BF%CF%83%CF%84%CE%B1%CF%83%CE%AF%CE%B1-%CE%B4%CE%B5%CE%B4%CE%BF%CE%BC%CE%AD%CE%BD%CF%89%CE%BD'
    case 'it-IT':
      return 'https://www.trivago.it/it/sp/politica-sulla-privacy'
    case 'ja-JP':
      return 'https://www.trivago.jp/ja/sp/%E3%83%97%E3%83%A9%E3%82%A4%E3%83%90%E3%82%B7%E3%83%BC%E3%83%9D%E3%83%AA%E3%82%B7%E3%83%BC'
    case 'es-MX':
      return 'https://www.trivago.com.mx/es-MX/sp/politica-de-privacidad'
    case 'pl-PL':
      return 'https://www.trivago.pl/pl/sp/polityka-prywatnosci'
    case 'pt-PT':
      return 'https://www.trivago.pt/pt/sp/politica-privacidade'
    case 'tr-TR':
      return 'https://www.trivago.com.tr/tr/sp/gizlilik-politikas%C4%B1'
    default:
      return 'https://www.trivago.com/en-US/sp/privacy-policy'
  }
}

/** Parsed date components */
export interface DateComponents {
  y: string
  m: number
  d: string
}

/**
 * Convert YYYYMMDD string to date components
 * @param str - Date string in YYYYMMDD format
 * @returns Date components or null if invalid
 */
export function convertYYYYMMDDtoObject(str: string): DateComponents | null {
  if (!/^(\d){8}$/.test(str)) return null

  const y = str.slice(0, 4)
  const m = parseInt(str.slice(4, 6), 10) - 1
  const d = str.slice(6, 8)

  return { y, m, d }
}

/**
 * Format date to YYYYMMDD string
 * @param date - Date object to format
 * @param delimiter - Optional delimiter between components
 * @returns Formatted date string
 */
export function formatYYYYMMDD(date: Date, delimiter = ''): string {
  const mm = date.getMonth() + 1
  const dd = date.getDate()

  return [date.getFullYear(), (mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd].join(delimiter)
}

/** Date formatting options */
const DATE_LOCALE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'long',
  day: '2-digit',
}

/**
 * Convert date to localized formatted string
 * @param date - Date to format
 * @param locale - Locale code
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export function convertDateToFormattedString(
  date: Date,
  locale = 'en-US',
  options: Intl.DateTimeFormatOptions = DATE_LOCALE_OPTIONS
): string {
  if (locale === 'es-MX') {
    options = { ...options, day: 'numeric' }
  }
  return date instanceof Date ? date.toLocaleDateString(locale, options) : ''
}

/** Item with expiration date */
export interface ExpiringItem {
  expirationDate?: string
  [key: string]: unknown
}

/**
 * Filter items by expiration date
 * Keeps items without expiration or with future expiration
 * @param items - Items to filter
 * @returns Non-expired items
 */
export function filteringByExpirationDate<T extends ExpiringItem>(items: T[] | undefined): T[] {
  const now = new Date()

  return items?.filter(item => {
    if (!item.expirationDate) {
      return true
    }

    const expirationDate = new Date(item.expirationDate)

    return expirationDate > now
  }) ?? []
}

/**
 * Get difference between two dates (in milliseconds)
 * Normalizes dates to midnight before comparison
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Difference in milliseconds
 */
export function getDatesDifference(date1: Date, date2: Date): number {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate())
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate())

  return d1.getTime() - d2.getTime()
}


// ============================================================================
// Lodash Replacements
// ============================================================================

/**
 * Sort array by property
 * Replaces: lodash/orderBy
 * @param array - Array to sort
 * @param key - Property to sort by
 * @param direction - Sort direction ('asc' or 'desc')
 * @returns New sorted array
 */
export function orderBy<T extends Record<string, any>>(
  array: T[],
  key: keyof T,
  direction: 'asc' | 'desc' = 'asc'
): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key]
    const bVal = b[key]
    if (direction === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    }
    return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
  })
}

/**
 * Debounce function calls
 * Replaces: lodash/debounce
 * @param fn - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>
  
  return function (this: any, ...args: Parameters<T>): void {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn.apply(this, args), delay)
  }
}

/**
 * Filter array by predicate
 * Replaces: lodash/filter
 * @param array - Array to filter
 * @param predicate - Filter function or truthy check
 * @returns Filtered array
 */
export function filter<T>(array: T[], predicate?: ((item: T) => boolean) | boolean): T[] {
  if (typeof predicate === 'function') {
    return array.filter(predicate)
  }
  // lodash filter(array, Boolean) - remove falsy values
  return array.filter(Boolean)
}

/**
 * Get maximum value from array
 * Replaces: lodash/max
 * @param array - Array of numbers
 * @returns Maximum value or undefined if empty
 */
export function max(array: number[]): number | undefined {
  if (!array || array.length === 0) return undefined
  return array.reduce((a, b) => Math.max(a, b))
}

/**
 * Deep clone object
 * Replaces: lodash/cloneDeep
 * 
 * Note: Uses JSON serialization which has limitations:
 * - Drops undefined values, functions, Symbols
 * - Converts Date to ISO string
 * - Cannot handle circular references, Map, Set
 * Suitable for simple Vue props and plain objects.
 * 
 * @param obj - Object to clone
 * @returns Cloned object
 */
export function cloneDeep<T>(obj: T): T {
  // Handle primitives and null
  if (obj === null || typeof obj !== 'object') return obj

  // For Vue objects, use JSON method (safe and fast)
  return JSON.parse(JSON.stringify(obj)) as T
}

/**
 * Deep equality check
 * Replaces: lodash/isEqual
 * @param a - First value
 * @param b - Second value
 * @returns True if deeply equal
 */
export function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return a === b

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime()
  }
  if (a instanceof Date || b instanceof Date) return false

  if (typeof a !== 'object' || typeof b !== 'object') return a === b

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((item, index) => isEqual(item, b[index]))
  }

  if (Array.isArray(a) || Array.isArray(b)) return false

  const keysA = Object.keys(a)
  const keysB = Object.keys(b)

  if (keysA.length !== keysB.length) return false

  return keysA.every(key => isEqual((a as any)[key], (b as any)[key]))
}

/**
 * Find element with minimum value by iteratee
 * Replaces: lodash/minBy
 * @param array - Array to search
 * @param iteratee - Function or property name to get value
 * @returns Element with minimum value, or undefined if array is empty
 */
export function minBy<T>(
  array: T[] | undefined,
  iteratee: ((item: T) => number) | keyof T
): T | undefined {
  if (!array || array.length === 0) return undefined

  // If iteratee is a function, use it; otherwise treat as property name
  const getValue = typeof iteratee === 'function' 
    ? iteratee 
    : (item: T) => item[iteratee] as unknown as number

  return array.reduce((min, item) => {
    const minVal = getValue(min)
    const itemVal = getValue(item)

    // Handle null/undefined/NaN
    if (minVal == null || isNaN(minVal)) return item
    if (itemVal == null || isNaN(itemVal)) return min

    return itemVal < minVal ? item : min
  })
}

// ============================================================================
//Formatting
// ============================================================================

/** Number formatting options (extends Intl options) */
export interface NumberFormatOptions extends Intl.NumberFormatOptions {
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

/** Currency formatting options */
export interface CurrencyFormatOptions extends Intl.NumberFormatOptions {
  /** Divide value by 100 before formatting (for cent values) */
  divideBy100?: boolean
}

/**
 * Format number with locale
 * @param value - Number to format
 * @param language - Locale code
 * @param options - Formatting options (Intl.NumberFormatOptions)
 * @returns Formatted number string or '-' if invalid
 */
export function formatNumber(
  value: number | null | undefined,
  language: string,
  options: NumberFormatOptions = {}
): string {
  const { minimumFractionDigits = 0, maximumFractionDigits = 2, ...numberFormatOptions } = options

  if (value === null || value === undefined || isNaN(value)) return '-'

  return new Intl.NumberFormat(language, {
    minimumFractionDigits,
    maximumFractionDigits,
    ...numberFormatOptions,
  }).format(value)
}

/**
 * Format currency with locale
 * @param value - Amount to format
 * @param language - Locale code
 * @param currency - Currency code (e.g., 'EUR', 'USD')
 * @param options - Formatting options (supports divideBy100 for cent values)
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: number | null | undefined,
  language: string,
  currency: string,
  options: CurrencyFormatOptions = {}
): string {
  const { divideBy100 = true, ...numberFormatOptions } = options

  if (value === null || value === undefined || isNaN(value)) return '-'

  const amount = divideBy100 ? value / 100 : value

  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency,
    ...numberFormatOptions,
  }).format(amount)
}

/** Date formatting options (extends Intl options) */
export interface DateFormatOptions extends Intl.DateTimeFormatOptions {}

/**
 * Format date with locale
 * @param date - Date to format (Date object or string)
 * @param language - Locale code
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string or '-' if invalid
 */
export function formatDate(
  date: Date | string | null | undefined,
  language: string,
  options: DateFormatOptions = {}
): string {
  if (date === null || date === undefined) return '-'

  const dateObj = date instanceof Date ? date : new Date(date)
  if (isNaN(dateObj.getTime())) return '-'

  return new Intl.DateTimeFormat(language, options).format(dateObj)
}

/**
 * Format date range with locale
 * @param startDate - Start date (Date object or string)
 * @param endDate - End date (Date object or string)
 * @param language - Locale code
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date range string or '-' if invalid
 */
export function formatDateRange(
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
  language: string,
  options: DateFormatOptions = {}
): string {
  const start = formatDate(startDate, language, options)
  const end = formatDate(endDate, language, options)

  if (start === '-' || end === '-') return '-'

  return `${start} - ${end}`
}

/**
 * Format number as percentage
 * @param locale - Locale for formatting
 * @param number - Number to format (0-100)
 * @param minDigits - Minimum fraction digits (0-20)
 * @param maxDigits - Maximum fraction digits (0-20)
 * @returns Formatted percentage string
 */
export function formatPercentage(
  locale: string,
  number: number,
  minDigits = 0,
  maxDigits = 0
): string {
  const safeMaxDigits = Math.max(0, Math.min(20, maxDigits || 0))
  const safeMinDigits = Math.max(0, Math.min(20, minDigits || 0))

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: safeMinDigits,
    maximumFractionDigits: safeMaxDigits,
  }).format(number / 100)
}
